import React, { useCallback, useEffect, useState } from "react";
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
  CardBody,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { CardContent } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AddendumModal from "./AddendumModal";
import AddendumSendModal from "./AddendumSendModal";
import AddendumDocument from "../../../../../../components/Addendum/AddendumDocument";
import { listAddendums, completeAddendum, voidAddendum, getAddendumSignedUrl } from "./api";
import { formatYmd } from "../../../../../../utils/dateOnly";

const STATUS_STYLE = {
  Draft: { bg: "#eef0f2", fg: "#5f5e5a", label: "Draft" },
  Sent: { bg: "#e6f1fb", fg: "#0c447c", label: "Awaiting signature" },
  PartiallySigned: { bg: "#faeeda", fg: "#854f0b", label: "Partially signed" },
  Signed: { bg: "#e1f5ee", fg: "#0f6e56", label: "Signed — live on start date" },
  Completed: { bg: "#eaf3de", fg: "#27500a", label: "Completed" },
  Voided: { bg: "#fcebeb", fg: "#a32d2d", label: "Voided" },
};

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const summarize = (a) => {
  if (Array.isArray(a.amendments) && a.amendments.length) {
    return (
      a.amendments
        .map((x) => x.display_name || x.label || x.field_key)
        .filter(Boolean)
        .join(" · ") || "Lease amendment"
    );
  }
  const parts = [];
  if (a.amendment_details?.parking?.selected) parts.push("Parking");
  if (a.amendment_details?.pet?.selected) parts.push(`Pet (${a.amendment_details.pet.description || "pet"})`);
  if ((a.amendment_details?.others || []).length) {
    parts.push(a.amendment_details.others.map((o) => o.label).filter(Boolean).join(" + ") || "Other");
  }
  return parts.join(" · ") || "Rent amendment";
};

const Badge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Draft;
  return (
    <span style={{ background: s.bg, color: s.fg, fontSize: 12, fontWeight: 600, padding: "4px 11px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-block" }}>
      {s.label}
    </span>
  );
};

const SignedDots = ({ addendum }) => {
  const sigs = addendum.tenant_signatures || [];
  const done = sigs.length > 0 && sigs.every((t) => t.signed);
  const count = sigs.filter((t) => t.signed).length;
  return (
    <span className={`adx-signed${done ? " done" : ""}`}>
      <span className="adx-dots">
        {sigs.map((t, i) => (
          <span key={i} className={`adx-dot${t.signed ? " on" : ""}`} title={`${t.name || "Tenant"}${t.signed ? " — signed" : " — pending"}`} />
        ))}
      </span>
      {count}/{sigs.length}
    </span>
  );
};

const AddendumsList = ({ itemId, item, tenants, fields, propertyId, buildingName, propertyName, unitNo }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const res = await listAddendums(itemId);
      setItems(res.data?.data || []);
    } catch (e) {
      toast.error("Failed to load addendums");
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleComplete = async (a) => {
    const confirm = await Swal.fire({
      title: "Apply addendum now?",
      text: "Signed addendums go live automatically on their start date. Completing now applies the new charges to the mastersheet immediately and emails all parties the final, fully-signed copy. You can void it later to revert.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, complete",
      confirmButtonColor: "#0f6e56",
    });
    if (!confirm.isConfirmed) return;
    setBusyId(a._id);
    try {
      const res = await completeAddendum(a._id, {});
      toast.success(res.data?.message || "Addendum completed");
      if (previewTarget?._id === a._id) setPreviewTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to complete");
    } finally {
      setBusyId(null);
    }
  };

  const handleVoid = async (a) => {
    const confirm = await Swal.fire({
      title: "Void this addendum?",
      text: a.status === "Completed" ? "The mastersheet will be reverted to the values before this addendum." : "This addendum will be cancelled.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, void",
      confirmButtonColor: "#a32d2d",
    });
    if (!confirm.isConfirmed) return;
    setBusyId(a._id);
    try {
      await voidAddendum(a._id, confirm.value);
      toast.success("Addendum voided");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to void");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (a) => {
    setBusyId(a._id);
    try {
      const res = await getAddendumSignedUrl(a._id);
      const url = res.data?.data?.url;
      if (url) window.open(url, "_blank");
      else toast.error("No signed PDF available yet");
    } catch (e) {
      toast.error(e?.response?.data?.message || "No signed PDF available yet");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <style>{`
        .adx-wrap { border: 1px solid #E4E9F2; border-radius: 12px; overflow: hidden; background: #fff; }
        .adx-scroll { overflow-x: auto; }
        .adx-table { width: 100%; min-width: 950px; border-collapse: collapse; font-size: 13.5px; color: #0F1F35; }
        .adx-table thead th { background: #F7F9FC; color: #64748B; font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
          text-transform: uppercase; text-align: left; padding: 11px 16px; border-bottom: 1px solid #E4E9F2; white-space: nowrap; }
        .adx-table thead th.num { text-align: right; }
        .adx-table tbody td { padding: 14px 16px; vertical-align: middle; border-bottom: 1px solid #EEF2F7; }
        .adx-table tbody tr:last-child td { border-bottom: none; }
        .adx-table tbody tr { transition: background .12s ease-out; }
        .adx-table tbody tr:hover { background: #FAFCFF; }
        .adx-table td.num { text-align: right; }
        .adx-name { font-weight: 600; font-size: 14px; line-height: 1.35; }
        .adx-id { font-size: 11.5px; color: #8A94A6; font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
          margin-top: 3px; letter-spacing: 0.02em; }
        .adx-date { white-space: nowrap; color: #3D4C63; font-variant-numeric: tabular-nums; }
        .adx-rent { font-weight: 700; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .adx-signed { display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; color: #64748B; font-variant-numeric: tabular-nums; }
        .adx-signed.done { color: #0F6E56; font-weight: 600; }
        .adx-dots { display: inline-flex; gap: 4px; }
        .adx-dot { width: 8px; height: 8px; border-radius: 50%; background: #DDE5EF; display: inline-block; }
        .adx-dot.on { background: #12A16F; }
        .adx-actions { display: flex; justify-content: flex-end; align-items: center; gap: 8px; white-space: nowrap; }
        .adx-btn { height: 30px; padding: 0 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px; border: 1px solid #D6DEEA; background: #fff; color: #33415C;
          transition: background .12s ease-out, border-color .12s ease-out; font-family: inherit; }
        .adx-btn:hover:not(:disabled) { background: #F1F5FA; border-color: #B9C6D8; }
        .adx-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .adx-btn-solid { background: #0C447C; border-color: #0C447C; color: #fff; }
        .adx-btn-solid:hover:not(:disabled) { background: #083460; border-color: #083460; }
        .adx-btn-blue { color: #0C447C; border-color: #C6D6E8; }
        .adx-btn-blue:hover:not(:disabled) { background: #EBF3FC; border-color: #9FBBDA; }
        .adx-btn-green { background: #0F6E56; border-color: #0F6E56; color: #fff; }
        .adx-btn-green:hover:not(:disabled) { background: #0B5843; border-color: #0B5843; }
        .adx-btn-danger { color: #A32D2D; border-color: #EDD3D3; }
        .adx-btn-danger:hover:not(:disabled) { background: #FCF1F1; border-color: #DFB3B3; }
      `}</style>
      <div className="d-flex justify-content-end mb-3">
        <RButton color="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
          + Add addendum
        </RButton>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" /> <span className="text-muted ms-2">Loading…</span>
        </div>
      ) : items.length === 0 ? (
        <Alert color="info" className="my-2">
          No addendums yet. Click “Add addendum” to create one.
        </Alert>
      ) : (
        <div className="adx-wrap">
          <div className="adx-scroll">
            <table className="adx-table">
              <thead>
                <tr>
                  <th className="num">S.No</th>
                  <th>Addendum</th>
                  <th>Status</th>
                  <th>Start date</th>
                  <th>Lease end</th>
                  <th>Signed</th>
                  <th className="num">New monthly rent</th>
                  <th className="num">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a, i) => {
                  const busy = busyId === a._id;
                  // Newest sits on top, so count up from the bottom — the number a
                  // given addendum carries never changes as new ones are added.
                  const sNo = items.length - i;
                  return (
                    <tr key={a._id}>
                      <td className="num">{sNo}</td>
                      <td style={{ minWidth: 210 }}>
                        <div className="adx-name">{summarize(a)}</div>
                        <div className="adx-id">{a.addendum_id}</div>
                      </td>
                      <td>
                        <Badge status={a.status} />
                      </td>
                      <td className="adx-date">{a.effective_date ? formatYmd(a.effective_date) : "—"}</td>
                      <td className="adx-date">{a.lease_end_date ? formatYmd(a.lease_end_date) : "—"}</td>
                      <td>
                        <SignedDots addendum={a} />
                      </td>
                      <td className="num adx-rent">{money(a.revised_rent?.new_total_monthly_rent)}</td>
                      <td>
                        <div className="adx-actions">
                          {busy && <Spinner size="sm" />}
                          <button className="adx-btn" onClick={() => setPreviewTarget(a)} disabled={busy}>
                            <FeatherIcon icon="eye" size={13} /> Preview
                          </button>
                          {a.status === "Draft" && (
                            <button className="adx-btn" onClick={() => { setEditing(a); setModalOpen(true); }} disabled={busy}>
                              <FeatherIcon icon="edit-2" size={13} /> Edit
                            </button>
                          )}
                          {a.status === "Draft" && (
                            <button className="adx-btn adx-btn-solid" onClick={() => setSendTarget(a)} disabled={busy}>
                              <FeatherIcon icon="send" size={13} /> Send
                            </button>
                          )}
                          {["Sent", "PartiallySigned"].includes(a.status) && (
                            <button className="adx-btn adx-btn-blue" onClick={() => setSendTarget(a)} disabled={busy}>
                              <FeatherIcon icon="send" size={13} /> Resend
                            </button>
                          )}
                          {a.status === "Signed" && (
                            <button className="adx-btn adx-btn-green" onClick={() => handleComplete(a)} disabled={busy}>
                              <FeatherIcon icon="check-circle" size={13} /> Apply now
                            </button>
                          )}
                          {a.status === "Completed" && (
                            <button className="adx-btn adx-btn-blue" onClick={() => handleDownload(a)} disabled={busy}>
                              <FeatherIcon icon="download" size={13} /> PDF
                            </button>
                          )}
                          {a.status !== "Voided" && (
                            <button className="adx-btn adx-btn-danger" onClick={() => handleVoid(a)} disabled={busy}>
                              Void
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <AddendumModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          itemId={itemId}
          item={item}
          tenants={tenants}
          fields={fields}
          buildingName={buildingName}
          propertyName={propertyName}
          unitNo={unitNo}
          defaults={{ property_id: propertyId }}
          existing={editing}
          onSaved={load}
        />
      )}

      {sendTarget && (
        <AddendumSendModal
          isOpen={!!sendTarget}
          toggle={() => setSendTarget(null)}
          addendum={sendTarget}
          onSent={load}
        />
      )}

      <Modal isOpen={!!previewTarget} toggle={() => setPreviewTarget(null)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setPreviewTarget(null)}>Addendum preview</ModalHeader>
        <ModalBody style={{ background: "#f4f6f8" }}>
          {previewTarget && <AddendumDocument addendum={previewTarget} />}
        </ModalBody>
        <ModalFooter>
          {previewTarget?.status === "Signed" && (
            <RButton color="success" onClick={() => handleComplete(previewTarget)}>
              Approve &amp; apply now
            </RButton>
          )}
          {previewTarget?.status === "Completed" && (
            <RButton color="primary" outline onClick={() => handleDownload(previewTarget)}>
              Download signed PDF
            </RButton>
          )}
          <RButton color="light" onClick={() => setPreviewTarget(null)}>
            Close
          </RButton>
        </ModalFooter>
      </Modal>
    </div>
  );
};

/**
 * Collapsible "Lease Addendums" section for the NewMasterSheet tenant modal —
 * mirrors the Lease History section (pill button + Collapse).
 */
const Addendums = ({ propertyId, buildingName, propertyName, unitNo, itemId, item, tenants, fields }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton color="primary" outline className="fw-bold px-4 py-2 rounded-pill" onClick={() => setIsOpen((p) => !p)}>
          <FeatherIcon icon="file-text" className="me-2" size={16} />
          Lease Addendums
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          {!itemId ? (
            <Alert color="info" className="my-2">
              Save the tenant first to create lease addendums for this unit.
            </Alert>
          ) : (
            <AddendumsList
              itemId={itemId}
              item={item}
              tenants={tenants}
              fields={fields}
              propertyId={propertyId}
              buildingName={buildingName}
              propertyName={propertyName}
              unitNo={unitNo}
            />
          )}
        </CardContent>
      </RCollapse>
    </RCard>
  );
};

export { Addendums };
export default Addendums;
